import { useCallback, useEffect, useMemo, useState } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { setDoc } from '../lib/firestore'
import { useFirestoreDoc } from './useFirestore'
import { useAuth } from './useAuth'
import { log } from '../lib/logger'
import { setAnalyticsUserProperty, track } from '../lib/analytics'
import { EVERYWHERE_REGION_ID, getRegion, regionForTimezone } from '@shared/regions'
import type { Region } from '@shared/regions'
import type { RegionSource, UserPreferences } from '@shared/preferences'

const STORAGE_KEY = 'beacon_home_region'
/** Tells every mounted useHomeRegion a choice was made, so the header chip and the page's list agree. */
const CHANGE_EVENT = 'beacon:home-region'

/** How long the Firestore mirror waits for the server snapshot to settle. */
const MIRROR_DELAY_MS = 5000

/** Where a `setRegion` call came from — analytics only, not persisted state. */
export type RegionChangeSource = 'settings' | 'location_chip' | 'travel_nudge' | 'deep_link'

interface StoredRegion {
  regionId: string
  source: RegionSource
}

function deviceTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

function readLocal(): StoredRegion | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredRegion>
    if (typeof parsed.regionId !== 'string') return null
    return {
      regionId: parsed.regionId,
      source: parsed.source === 'manual' ? 'manual' : 'auto',
    }
  } catch {
    return null
  }
}

function writeLocal(value: StoredRegion) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Private-mode Safari and similar. The in-memory value still works for
    // this session; it just won't survive a reload.
    log('region', 'localStorage write failed')
  }
}

/**
 * Resolve the region to use on first run, or whenever an `auto` region should
 * re-follow the device. Never returns undefined — an uncovered timezone maps
 * to `everywhere`, so there is no unconfigured state.
 */
function detectRegion(): StoredRegion {
  return { regionId: regionForTimezone(deviceTimezone()), source: 'auto' }
}

function initialRegion(): StoredRegion {
  const stored = readLocal()
  if (!stored) return detectRegion()
  // An `auto` region keeps tracking the device: travel, and it moves with you.
  // A `manual` one is the user's explicit choice and is never overridden.
  if (stored.source === 'auto') {
    const detected = detectRegion()
    if (detected.regionId !== stored.regionId) return detected
  }
  return stored
}

/**
 * The region whose events the user is browsing, plus how it was chosen.
 *
 * localStorage is the primary store — instant, offline-safe, and it works for
 * guests, who are a large share of arrivals. For signed-in users it also
 * mirrors to Firestore so the choice follows them across devices.
 */
export function useHomeRegion(): {
  regionId: string
  region: Region | undefined
  source: RegionSource
  setRegion: (regionId: string, from: RegionChangeSource) => void
  isEverywhere: boolean
} {
  const { signedInUid } = useAuth()
  const [stored, setStored] = useState<StoredRegion>(initialRegion)

  const ref = useMemo(
    () =>
      signedInUid
        ? (doc(
            db,
            'users',
            signedInUid,
            'private',
            'preferences',
          ) as DocumentReference<UserPreferences>)
        : null,
    [signedInUid],
  )
  const { data: remote, loading: remoteLoading } = useFirestoreDoc<UserPreferences>(ref)

  useEffect(() => {
    const onChange = (e: Event) => setStored((e as CustomEvent<StoredRegion>).detail)
    window.addEventListener(CHANGE_EVENT, onChange)
    return () => window.removeEventListener(CHANGE_EVENT, onChange)
  }, [])

  // A user property, so every GA4 event can be split by the viewer's region.
  useEffect(() => setAnalyticsUserProperty('region_id', stored.regionId), [stored.regionId])

  // Persist whatever we resolved locally, including the first-run detection, so
  // the same value is used before Firestore answers on the next load.
  useEffect(() => {
    const current = readLocal()
    if (current?.regionId !== stored.regionId || current?.source !== stored.source) {
      writeLocal(stored)
    }
  }, [stored])

  // Adopt a manual choice made on another device. An `auto` remote value is
  // ignored: it reflects where that device was, not where this one is.
  //
  // Applied during render rather than in an effect — the remote value arrives
  // asynchronously, and setting state from an effect would render the old
  // region once before correcting it. `trackedRemote` makes it idempotent so a
  // resubscribe delivering the same value doesn't undo a newer local choice.
  const remoteChoice =
    remote?.regionId && remote.regionSource === 'manual' ? remote.regionId : null
  const [trackedRemote, setTrackedRemote] = useState<string | null>(remoteChoice)
  if (trackedRemote !== remoteChoice) {
    setTrackedRemote(remoteChoice)
    if (remoteChoice && remoteChoice !== stored.regionId) {
      setStored({ regionId: remoteChoice, source: 'manual' })
    }
  }

  // Mirror the resolved region to Firestore even when it was auto-detected.
  // The adoption above deliberately ignores a remote `auto` value — it says
  // where that *device* was — but the server has no other way to know where to
  // send an artist-match notification (#380), and most people never open the
  // picker, so a manual-only mirror leaves almost everyone unreachable.
  //
  // Deliberately settles first. `onSnapshot` answers from the offline cache
  // before the server, so on a fresh device `remote` reads as absent for a
  // moment even when another device stored a manual choice — writing
  // immediately would clobber it and then flap back. Nothing depends on this
  // landing promptly, so it waits for the server snapshot to arrive.
  useEffect(() => {
    if (!ref || remoteLoading) return
    if (remote?.regionId === stored.regionId && remote?.regionSource === stored.source) return
    // Never downgrade someone's explicit choice back to `auto`.
    if (remote?.regionSource === 'manual' && stored.source === 'auto') return
    const timer = setTimeout(() => {
      void setDoc(
        ref,
        { regionId: stored.regionId, regionSource: stored.source },
        { merge: true },
      ).catch(() => {
        log('region', 'failed to mirror region preference')
      })
    }, MIRROR_DELAY_MS)
    return () => clearTimeout(timer)
  }, [ref, remoteLoading, remote?.regionId, remote?.regionSource, stored])

  const setRegion = useCallback(
    (regionId: string, from: RegionChangeSource) => {
      const next: StoredRegion = { regionId, source: 'manual' }
      setStored(next)
      writeLocal(next)
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }))
      setAnalyticsUserProperty('region_id', regionId)
      track('home_region_set', { entry_point: from })
      if (ref) {
        void setDoc(ref, { regionId, regionSource: 'manual' }, { merge: true }).catch(() => {
          // The local value is already applied; a failed sync just means this
          // choice doesn't reach the user's other devices.
          log('region', 'failed to sync region preference')
        })
      }
    },
    [ref],
  )

  return {
    regionId: stored.regionId,
    region: getRegion(stored.regionId),
    source: stored.source,
    setRegion,
    isEverywhere: stored.regionId === EVERYWHERE_REGION_ID,
  }
}
